/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The recording and transcript for one call, without any card chrome.
 *
 * Extracted from CallDetail so the console and the CRM detail pages render the
 * same thing rather than three copies of it. Chrome is left to the host,
 * because the two grammars in this app disagree: the call center wraps this in
 * InfoCard, the CRM detail pages in the older gradient-header Card.
 *
 * The player is a plain <audio controls>. A custom waveform would look better
 * and do nothing an agent needs; the browser's own controls already handle
 * seeking, speed and keyboard access.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MonoText from "components/MonoText";
import StatusBadge from "components/StatusBadge";
import { useAuth } from "auth/Auth";
import { formatDuration } from "telephony/callStates";
import { getApiErrorMessage } from "utils/apiErrors";

function timestamp(seconds) {
  return formatDuration(Math.floor(seconds || 0));
}

export default function ConversationRecording({ conversationId, autoLoadRecording }) {
  const { axiosInstance } = useAuth();
  const audioRef = useRef(null);

  const [recording, setRecording] = useState(null);
  const [recordingError, setRecordingError] = useState("");
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(true);

  // Signed URLs expire, so this is fetched on demand rather than with the page.
  const loadRecording = useCallback(async () => {
    if (!conversationId) return;
    setRecordingError("");
    setLoadingRecording(true);
    try {
      const res = await axiosInstance.get(`/telephony/calls/${conversationId}/recording`);
      setRecording(res.data);
    } catch (err) {
      setRecordingError(
        getApiErrorMessage(err, "No recording is available for this call.")
      );
    } finally {
      setLoadingRecording(false);
    }
  }, [axiosInstance, conversationId]);

  useEffect(() => {
    // Reset on id change, or an expanded row would briefly show the previous
    // call's audio and transcript under the new call's heading.
    setRecording(null);
    setRecordingError("");
    setTranscript(null);
    if (!conversationId) return undefined;

    let cancelled = false;
    setLoadingTranscript(true);
    axiosInstance
      .get(`/telephony/calls/${conversationId}/transcript`)
      .then((r) => {
        if (!cancelled) setTranscript(r.data);
      })
      .catch(() => {
        if (!cancelled) setTranscript(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingTranscript(false);
      });
    return () => {
      cancelled = true;
    };
  }, [axiosInstance, conversationId]);

  useEffect(() => {
    if (autoLoadRecording && conversationId) loadRecording();
  }, [autoLoadRecording, conversationId, loadRecording]);

  const seekTo = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = seconds || 0;
      audioRef.current.play();
    }
  };

  const transcriptStatus = transcript?.status;

  return (
    <MDBox>
      {/* ---------------------------------------------------- recording */}
      <MDBox mb={2.5}>
        <MDBox display="flex" alignItems="center" gap={1} mb={1}>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Recording
          </MDTypography>
        </MDBox>

        {recordingError && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {recordingError}
          </Alert>
        )}

        {recording ? (
          <MDBox>
            <audio
              ref={audioRef}
              controls
              preload="none"
              src={recording.url}
              style={{ width: "100%" }}
            >
              <track kind="captions" />
            </audio>
            <MDTypography variant="caption" color="text" display="block" mt={0.5}>
              {recording.duration_seconds
                ? `${formatDuration(recording.duration_seconds)} · `
                : ""}
              This playback link expires shortly.
            </MDTypography>
          </MDBox>
        ) : (
          !recordingError && (
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={loadRecording}
              disabled={loadingRecording}
            >
              {loadingRecording ? "Loading…" : "Load recording"}
            </MDButton>
          )
        )}
      </MDBox>

      {/* --------------------------------------------------- transcript */}
      <MDBox>
        <MDBox display="flex" alignItems="center" gap={1} mb={1}>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Transcript
          </MDTypography>
          {transcriptStatus && transcriptStatus !== "none" && (
            <StatusBadge status={transcriptStatus} label={transcriptStatus} size="xs" />
          )}
        </MDBox>

        {loadingTranscript ? (
          <CircularProgress size={18} color="info" />
        ) : !transcript || transcriptStatus === "none" ? (
          <MDTypography variant="button" color="text">
            No transcript for this call.
          </MDTypography>
        ) : transcriptStatus !== "completed" ? (
          <MDTypography variant="button" color="text">
            {transcriptStatus === "failed"
              ? "Transcription failed for this call."
              : "Transcription in progress…"}
          </MDTypography>
        ) : (
          <MDBox>
            {(transcript.segments || []).length > 0 ? (
              <MDBox
                display="flex"
                flexDirection="column"
                gap={1}
                sx={{ maxHeight: 320, overflowY: "auto" }}
              >
                {transcript.segments.map((seg, i) => (
                  <MDBox
                    key={`${seg.start}-${i}`}
                    display="flex"
                    gap={1.5}
                    onClick={() => seekTo(seg.start)}
                    sx={{
                      cursor: recording ? "pointer" : "default",
                      p: 0.75,
                      borderRadius: "6px",
                      "&:hover": recording ? { bgcolor: "#FAFBFD" } : undefined,
                    }}
                  >
                    <MonoText variant="caption" sx={{ color: "#5C6B82", minWidth: 48 }}>
                      {timestamp(seg.start)}
                    </MonoText>
                    <MDTypography variant="button" color="dark">
                      {seg.text}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            ) : (
              <MDTypography variant="button" color="dark">
                {transcript.text}
              </MDTypography>
            )}
            {transcript.diarized === false && (
              <MDTypography variant="caption" color="text" display="block" mt={2}>
                Speakers are not separated: conference audio is recorded as a single
                mixed track.
              </MDTypography>
            )}
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

ConversationRecording.propTypes = {
  conversationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // The console already knows the agent wants this call, so it can skip the
  // extra click. A list of past calls should not: auto-loading would mint a
  // signed URL for every row the user merely scrolled past.
  autoLoadRecording: PropTypes.bool,
};

ConversationRecording.defaultProps = {
  conversationId: null,
  autoLoadRecording: false,
};
